import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { LocaleSwitcher } from "@/knowmeno/marketing/LocaleSwitcher";

/**
 * Shared Spanish-page placeholder for marketing pages still being authored.
 * Renders a polished "coming soon" shell so links don't 404 before launch.
 */
export function EsPlaceholder({
  title,
  subtitle,
  englishHref,
}: {
  title: string;
  subtitle: string;
  englishHref?: string;
}) {
  return (
    <div className="min-h-screen bg-background" lang="es">
      <header className="border-b">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <Link to="/es" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> SepaMeno
          </Link>
          <LocaleSwitcher />
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-20 text-center">
        <span className="inline-block rounded-full bg-blush/30 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-plum">
          Próximamente
        </span>
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl">{title}</h1>
        <p className="mt-4 text-lg text-muted-foreground">{subtitle}</p>
        {englishHref && (
          <p className="mt-8 text-sm text-muted-foreground">
            ¿Lees inglés? Mientras tanto puedes ver la versión en inglés:{" "}
            <a href={englishHref} className="font-semibold text-primary hover:underline">
              {englishHref}
            </a>
          </p>
        )}
        <Link
          to="/es"
          className="mt-10 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          Volver al inicio
        </Link>
      </main>
    </div>
  );
}

/** Hook factory — gives each route a consistent canonical pair. */
export function esHead(opts: {
  path: string; // e.g. "/tienda"
  title: string;
  description: string;
  englishPath?: string; // e.g. "/store"
}) {
  const esUrl = `https://sepameno.com${opts.path}`;
  const enUrl = opts.englishPath ? `https://knowmeno.com${opts.englishPath}` : "https://knowmeno.com/";
  return {
    meta: [
      { title: opts.title },
      { name: "description", content: opts.description },
      { property: "og:title", content: opts.title },
      { property: "og:description", content: opts.description },
      { property: "og:url", content: esUrl },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://knowmeno.com/og-image.png?v=2" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "es" },
      { name: "robots", content: "index, follow" },
    ],
    links: [
      { rel: "canonical", href: esUrl },
      { rel: "alternate", hrefLang: "es", href: esUrl },
      { rel: "alternate", hrefLang: "en", href: enUrl },
      { rel: "alternate", hrefLang: "x-default", href: enUrl },
    ],
  };
}

